/**
 * ItemProfitScreen — margin per SKU and per category, and the products being
 * sold below cost. Sorted by margin, because that is what tells a shopkeeper
 * what is worth stocking.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadItemProfitability } from '@/reports/itemProfit/itemProfitDataSource';
import { buildItemProfitability, type ItemProfitability } from '@/reports/itemProfit/itemProfitability';

const pct = (fraction: number) => `${(fraction * 100).toFixed(1)}%`;

// ─── Component ───────────────────────────────────────────────────────────────

export function ItemProfitScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<ItemProfitability | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);
  const [view, setView]             = useState<'items' | 'categories'>('items');

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      const source = await loadItemProfitability(activeStoreId, from, to);
      setData(buildItemProfitability(source));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = view === 'items'
        ? toCsv(data.items, [
            { header: 'Product', value: (r) => r.name },
            { header: 'Category', value: (r) => r.category },
            { header: 'Qty', value: (r) => r.quantity },
            { header: 'Revenue', value: (r) => r.revenue },
            { header: 'COGS', value: (r) => r.cogs },
            { header: 'Margin', value: (r) => r.margin },
            { header: 'Margin %', value: (r) => (r.marginPct * 100).toFixed(1) },
            { header: 'Cost basis', value: (r) => (r.costMissing ? 'MISSING' : r.linesWithoutCost > 0 ? 'PARTIAL' : 'full') },
          ])
        : toCsv(data.categories, [
            { header: 'Category', value: (r) => r.category },
            { header: 'Qty', value: (r) => r.quantity },
            { header: 'Revenue', value: (r) => r.revenue },
            { header: 'COGS', value: (r) => r.cogs },
            { header: 'Margin', value: (r) => r.margin },
            { header: 'Margin %', value: (r) => (r.marginPct * 100).toFixed(1) },
          ]);
      await file.saveText({
        content: csv,
        defaultFileName: `ItemProfit_${view}_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildItemProfitHtml(data, from, to),
        defaultFileName: `ItemProfit_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            className={cn(
              'rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle',
            )}
          >
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.items.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">No sales in this period.</div>
      ) : (
        <>
          {/* Cost-basis disclosure */}
          {data.linesWithoutCost > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning-ink" />
              <div className="text-body text-fg-secondary">
                <span className="font-medium text-fg-primary">{pct(data.linesWithoutCostShare)} of lines have no cost basis</span>{' '}
                ({data.linesWithoutCost} of {data.lineCount}), covering {pct(data.revenueWithoutCostShare)} of sale value.
                Products marked <span className="font-medium">no cost</span> show revenue as margin — treat their margin as unknown.
                A negative margin is still reliable.
              </div>
            </div>
          )}

          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Revenue (ex-GST)" value={formatCurrency(data.totals.revenue)} />
            <KPI label="COGS" value={formatCurrency(data.totals.cogs)} />
            <KPI label="Gross margin" value={formatCurrency(data.totals.margin)} sub={pct(data.totals.marginPct)} accent />
            <KPI label="Sold below cost" value={String(data.belowCost.length)} warn={data.belowCost.length > 0} />
          </div>

          {/* Below-cost alert */}
          {data.belowCost.length > 0 && (
            <Card title="Sold below cost">
              <table className="w-full text-body">
                <tbody>
                  {data.belowCost.slice(0, 8).map((it) => (
                    <tr key={it.productId} className="border-b border-stroke-hairline/10 last:border-0">
                      <td className="py-1.5 text-fg-primary">{it.name}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{it.quantity} sold</td>
                      <td className="py-1.5 text-right tabular-nums text-warning-ink">{formatCurrency(it.margin)}</td>
                      <td className="py-1.5 text-right tabular-nums text-warning-ink">{pct(it.marginPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Items / categories toggle */}
          <div className="flex items-center gap-2">
            {(['items', 'categories'] as const).map((v) => (
              <button key={v} type="button" onClick={() => setView(v)}
                className={cn('rounded-md px-3 py-1 text-body transition-colors',
                  view === v ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
                {v === 'items' ? 'By product' : 'By category'}
              </button>
            ))}
          </div>

          <Card title={view === 'items' ? 'Margin by product' : 'Margin by category'}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">{view === 'items' ? 'Product' : 'Category'}</th>
                    <th className="pb-1 text-right font-normal">Qty</th>
                    <th className="pb-1 text-right font-normal">Revenue</th>
                    <th className="pb-1 text-right font-normal">COGS</th>
                    <th className="pb-1 text-right font-normal">Margin</th>
                    <th className="pb-1 text-right font-normal">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {view === 'items'
                    ? data.items.map((it) => (
                        <tr key={it.productId} className="border-b border-stroke-hairline/10">
                          <td className="py-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-fg-primary">{it.name}</span>
                              {it.costMissing && (
                                <span title="No cost basis — margin unknown"
                                  className="rounded bg-warning/10 px-1 text-label text-warning-ink">no cost</span>
                              )}
                            </div>
                            <div className="text-label text-fg-tertiary">{it.category}</div>
                          </td>
                          <td className="py-1.5 text-right tabular-nums text-fg-secondary">{it.quantity}</td>
                          <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(it.revenue)}</td>
                          <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{it.costMissing ? '–' : formatCurrency(it.cogs)}</td>
                          <MarginCells margin={it.margin} marginPct={it.marginPct} unknown={it.costMissing} />
                        </tr>
                      ))
                    : data.categories.map((c) => (
                        <tr key={c.category} className="border-b border-stroke-hairline/10">
                          <td className="py-1.5 text-fg-primary">{c.category}</td>
                          <td className="py-1.5 text-right tabular-nums text-fg-secondary">{c.quantity}</td>
                          <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(c.revenue)}</td>
                          <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{formatCurrency(c.cogs)}</td>
                          <MarginCells margin={c.margin} marginPct={c.marginPct} />
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function MarginCells({ margin, marginPct, unknown }: { margin: number; marginPct: number; unknown?: boolean }) {
  const cls = margin < 0 ? 'text-warning-ink' : 'text-fg-primary';
  return (
    <>
      <td className={cn('py-1.5 text-right font-medium tabular-nums', cls)}>{formatCurrency(margin)}</td>
      <td className={cn('py-1.5 text-right tabular-nums', margin < 0 ? 'text-warning-ink' : 'text-fg-tertiary')}>
        {unknown ? '—' : pct(marginPct)}
      </td>
    </>
  );
}

function KPI({ label, value, sub, accent, warn }: { label: string; value: string; sub?: string; accent?: boolean; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', warn ? 'text-warning-ink' : accent ? 'text-accent-ink' : 'text-fg-primary')}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-label text-fg-tertiary">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildItemProfitHtml(d: ItemProfitability, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

  const itemRows = d.items.slice(0, 100).map((it) =>
    `<tr><td>${esc(it.name)}${it.costMissing ? ' <span style="color:#b45309">(no cost)</span>' : ''}</td>
     <td>${esc(it.category)}</td>
     <td style="text-align:right">${it.quantity}</td>
     <td style="text-align:right">${fmt(it.revenue)}</td>
     <td style="text-align:right">${it.costMissing ? '–' : fmt(it.cogs)}</td>
     <td style="text-align:right${it.margin < 0 ? ';color:#b45309' : ''}">${fmt(it.margin)}</td>
     <td style="text-align:right">${it.costMissing ? '—' : (it.marginPct * 100).toFixed(1) + '%'}</td></tr>`,
  ).join('');

  const catRows = d.categories.map((c) =>
    `<tr><td>${esc(c.category)}</td><td style="text-align:right">${fmt(c.revenue)}</td>
     <td style="text-align:right">${fmt(c.cogs)}</td>
     <td style="text-align:right${c.margin < 0 ? ';color:#b45309' : ''}">${fmt(c.margin)}</td>
     <td style="text-align:right">${(c.marginPct * 100).toFixed(1)}%</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}h2{font-size:13px;margin-top:22px;text-transform:uppercase;color:#666}
table{width:100%;border-collapse:collapse;font-size:11px;margin-top:8px}th,td{padding:5px 7px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:first-child,td:first-child,th:nth-child(2),td:nth-child(2){text-align:left}</style></head>
<body>
<h1>Item &amp; Category Profitability</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)} · Revenue ${fmt(d.totals.revenue)} · COGS ${fmt(d.totals.cogs)} · Margin ${fmt(d.totals.margin)} (${(d.totals.marginPct * 100).toFixed(1)}%)</p>
${d.linesWithoutCost > 0 ? `<p style="color:#b45309;font-size:12px">Note: ${(d.linesWithoutCostShare * 100).toFixed(1)}% of lines have no cost basis — items marked "(no cost)" show revenue as margin.</p>` : ''}
<h2>By category</h2>
<table><thead><tr><th>Category</th><th>Revenue</th><th>COGS</th><th>Margin</th><th>Margin %</th></tr></thead><tbody>${catRows}</tbody></table>
<h2>By product</h2>
<table><thead><tr><th>Product</th><th>Category</th><th>Qty</th><th>Revenue</th><th>COGS</th><th>Margin</th><th>Margin %</th></tr></thead><tbody>${itemRows}</tbody></table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
